import dynamic from "next/dynamic";
import { Italiana } from "next/font/google";
import { useRouter } from "next/router";
import React from "react";

import Button from "@/components/core/button";
import useCollection from "@/hooks/useCollection";
import useMe from "@/hooks/useMe";
import MenuIcon from "@/public/menu.svg";

const Popover = dynamic(
  () => import("@nextui-org/popover").then((mod) => mod.Popover),
  { ssr: false }
);

const PopoverContent = dynamic(
  () => import("@nextui-org/popover").then((mod) => mod.PopoverContent),
  { ssr: false }
);

const PopoverTrigger = dynamic(
  () => import("@nextui-org/popover").then((mod) => mod.PopoverTrigger),
  { ssr: false }
);

const italiana = Italiana({
  subsets: ["latin"],
  weight: "400",
});

const HeaderMenu = () => {
  const { me, logout } = useMe();
  const { collections } = useCollection();
  const router = useRouter();

  const [isOpen, setIsOpen] = React.useState(false);

  const collectionCount = React.useMemo(() => {
    return collections.length > 0
      ? collections[0].totalCount > 99
        ? "99+"
        : collections[0].totalCount
      : 0;
  }, [collections]);

  const goPage = (page: string) => {
    if (page === "login") {
      router.push(`/login?redirectURL=${router.asPath}`);
    } else {
      router.push(`/${page}`);
    }
    setIsOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  return (
    <>
      <Popover
        placement="bottom-end"
        isOpen={isOpen}
        onOpenChange={(open) => setIsOpen(open)}
        offset={20}
        classNames={{ content: ["header-menu"] }}
      >
        <PopoverTrigger>
          <div className="header-menu-trigger">
            <MenuIcon />
          </div>
        </PopoverTrigger>
        <PopoverContent>
          <div className="header-menu-content">
            {me && (
              <div className="user">
                <div className={`${italiana.className} name`}>{me.name}</div>
              </div>
            )}
            {!me && (
              <div className="sign-in">
                <Button onClick={() => goPage("login")}>Sign in</Button>
              </div>
            )}
            <div className="option-list">
              <div className="option" onClick={() => goPage("")}>
                <img src="/home.svg" className="option-img" />
                <div className="option-text">Home</div>
              </div>
              {me && (
                <div className="option" onClick={() => goPage("collections")}>
                  <img src="/heart-line-black.svg" className="option-img" />
                  <div className="option-text">My collections</div>
                  <div className="option-right">{collectionCount}</div>
                </div>
              )}
              <div className="option" onClick={() => goPage("about")}>
                <img src="/about.svg" className="option-img" />
                <div className="option-text">About Plush</div>
              </div>
              <div className="option" onClick={() => goPage("contact")}>
                <img src="/contact.svg" className="option-img" />
                <div className="option-text">Contact</div>
              </div>
              {me && (
                <div className="option" onClick={handleLogout}>
                  <img src="/logout.svg" className="option-img" />
                  <div className="option-text text-red">Sign out</div>
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <style jsx>{`
        .header-menu-content {
          padding: 0 24px;
          background-color: #fff;
          width: 329px;
          box-shadow: 0px 16px 40px 0px rgba(0, 0, 0, 0.12);

          .user {
            width: 100%;
            text-align: center;
            padding: 24px 0;

            .name {
              color: #191919;
              font-size: 32px;
              font-style: normal;
              font-weight: 400;
              line-height: normal;
            }
          }

          .sign-in {
            padding-top: 24px;
          }
          .option-list {
            width: 100%;
            padding: 12px 0;

            .option {
              height: 48px;
              display: flex;
              align-items: center;
              cursor: pointer;
              position: relative;

              .option-img {
                width: 16px;
                height: 16px;
                margin-right: 12px;
              }

              .option-text {
                color: #191919;
                font-size: 14px;
                font-style: normal;
                font-weight: 500;
                line-height: normal;
                letter-spacing: -0.28px;

                &.text-red {
                  color: #a20000;
                }
              }

              .option-right {
                position: absolute;
                right: 0;

                color: #666;
                font-size: 14px;
                font-style: normal;
                font-weight: 500;
                line-height: normal;
                letter-spacing: -0.28px;
              }
            }
          }
        }
      `}</style>
      <style jsx global>
        {`
          .header-menu-trigger {
            position: absolute;
            top: 50px;
            right: 72px;
            z-index: 9999;
            cursor: pointer;

            @media (max-width: 768px) {
              display: none;
            }
          }
        `}
      </style>
    </>
  );
};

export default HeaderMenu;
